import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import Modal from "react-modal";
import {
  X,
  Calendar,
  User,
  Clock,
  Timer,
  Activity,
  CheckCircle,
  AlertTriangle,
  Eye,
  Loader2,
  Flag,
} from "lucide-react";
import { getUserRole } from "../utils/Auth";
import API_BASE_URL from "../config";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { PencilLine, Check, XCircle } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import ConfirmArchiveModal from "./ConfirmArchiveModal";

// helyi időt mutat, még ha a backend UTC-t is küld
// mindig helyi időt mutat (nem UTC-t)
// mindig a felhasználó saját helyi időzónáját mutatja
// Mindig a felhasználó saját helyi időzónáját mutatja, UTC-adatbázisból
const formatDate = (date) => {
  if (!date) return "—";
  try {
    // mindig biztosítjuk, hogy UTC-nek értelmezze
    const iso = typeof date === "string"
      ? (date.endsWith("Z") ? date : date + "Z")
      : date.toISOString();

    const d = new Date(iso);

    // automatikusan detektálja a böngésző időzónáját
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return d.toLocaleString("ro-RO", {
      timeZone: userTimeZone, // 🇷🇴/🇭🇺/🇺🇸 mindenkinek saját zóna
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const calcDuration = (start, end) => {
  if (!start || !end) return null;
  const diffMs = end - start;
  if (diffMs <= 0) return null;
  const totalMinutes = Math.floor(diffMs / (1000 * 60));
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  return { days, hours, minutes };
};

// 🔧 helper – csak akkor mutat toastot, ha nem aktív
const safeToast = {
  success: (msg, id) => {
    if (!toast.isActive(id)) toast.success(msg, { toastId: id });
  },
  error: (msg, id) => {
    if (!toast.isActive(id)) toast.error(msg, { toastId: id });
  },
};

const TaskDetailsModal = ({ isOpen, onClose, task, onTaskUpdated }) => {
  const [localTask, setLocalTask] = useState(task || null);
  const [users, setUsers] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [priorityOpen, setPriorityOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [priorityDropdownPos, setPriorityDropdownPos] = useState({
    top: 0,
    left: 0,
  });
  const role = getUserRole();
  const token = localStorage.getItem("token");
  const [editingDesc, setEditingDesc] = useState(false);
  const { darkMode } = useTheme();
  const [confirmArchiveOpen, setConfirmArchiveOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);

  // ha task prop változik → frissítsük a localTask state-et
  useEffect(() => {
    if (!isOpen) setEditingDesc(false);
  }, [isOpen]);

  useEffect(() => {
    setLocalTask(task || null);
    setEditingDesc(false);
  }, [task]);

  useEffect(() => {
    if (!priorityOpen) return;

    const handleClickOutside = (e) => {
      const dropdown = document.querySelector(".priority-dropdown");
      if (dropdown && !dropdown.contains(e.target)) {
        setPriorityOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [priorityOpen]);

  useEffect(() => {
    if (role !== "Admin" || !token) return;
    const fetchUsers = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/users`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setUsers(data);
      } catch (err) {
        console.error("⚠️ User fetch error:", err);
      }
    };
    fetchUsers();
  }, [role, token]);

  if (!localTask) return null;

  // --- Assign ---
  const assignUserToTask = async (taskId, agentId, agentName) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/assign`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ agentId }),
      });
      const data = await res.json();
      if (!res.ok || data.success === false) {
        safeToast.error(
          data.message || "Eroare la atribuirea utilizatorului.",
          "assign-error"
        );
        return;
      }
      safeToast.success("Utilizator atribuit cu succes!", "assign-success");
      setLocalTask((prev) => ({ ...prev, agentName }));
      setAssignOpen(false);

      if (typeof onTaskUpdated === "function") {
        onTaskUpdated(localTask.id, { agentName });
      }
    } catch (err) {
      console.error("⚠️ Assign user error:", err);
      safeToast.error("Eroare la server.", "assign-server");
    }
  };

  // --- Update priority ---
  const updatePriority = async (taskId, priorityValue) => {
    const priorityMap = { Urgent: 0, Mare: 1, Medie: 2, Mica: 3 };
    const numericValue = priorityMap[priorityValue];
    if (numericValue === undefined) {
      safeToast.error("Prioritate invalidă.", "priority-invalid");
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/priority`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ priority: numericValue }),
      });
      const data = await res.json();
      if (!res.ok || data.success === false) {
        safeToast.error(
          data.message || "Eroare la actualizarea priorității.",
          "priority-error"
        );
        return;
      }
      safeToast.success("Prioritate actualizată cu succes!", "priority-success");
      setLocalTask((prev) => ({ ...prev, priority: priorityValue }));
      setPriorityOpen(false);
      if (typeof onTaskUpdated === "function") {
        onTaskUpdated(localTask.id, { priority: priorityValue });
      }
    } catch (err) {
      console.error("⚠️ Update priority error:", err);
      safeToast.error("Eroare la server.", "priority-server");
    }
  };

  const saveDescription = async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/tasks/${localTask.id}/description`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ description: localTask.description }),
        }
      );
      const data = await res.json();
      if (!res.ok || data.success === false) {
        toast.error(data.message || "Eroare la actualizarea descrierii.");
        return;
      }

      toast.success("Descriere actualizată cu succes!", { toastId: "desc-success" });
      setEditingDesc(false);
      if (typeof onTaskUpdated === "function") {
        onTaskUpdated(localTask.id, { description: localTask.description });
      }
    } catch (err) {
      console.error("⚠️ Description update error:", err);
      toast.error("Eroare la server.");
    }
  };

  // --- Update deadline ---
  // --- Update deadline ---
  const updateDeadline = async (date) => {
    if (!date) return;

    const utcDate = date.toISOString(); // 🔹 pontosan így küldjük mint TaskBoard

    try {
      const res = await fetch(`${API_BASE_URL}/api/tasks/${localTask.id}/deadline`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ deadline: utcDate }),
      });

      const data = await res.json();
      if (!res.ok || data.success === false) {
        toast.error(data.message || "Eroare la actualizarea deadline-ului.");
        return;
      }

      toast.success("Deadline actualizat cu succes!");
      setLocalTask((prev) => ({ ...prev, deadline: utcDate }));
      if (typeof onTaskUpdated === "function") {
        onTaskUpdated(localTask.id, { deadline: utcDate });
      }
    } catch (err) {
      console.error("⚠️ Deadline update error:", err);
      toast.error("Eroare la server.");
    }
  };

  // --- Date helpers ---
  // --- Date helpers (helyi idő megjelenítés + pontos SLA számítás) ---
  const parseDate = (d) => {
    if (!d) return null;
    const iso = typeof d === "string"
      ? (d.endsWith("Z") ? d : d + "Z")
      : d.toISOString();
    return new Date(iso);
  };

  const createdAt = parseDate(localTask.createdAt);
  const deadline = parseDate(localTask.deadline);
  const startTime = parseDate(localTask.startTime);
  const completedAt = parseDate(localTask.completedAt);

  const sla = calcDuration(createdAt, deadline);
  const worked = calcDuration(startTime, completedAt);


  const formatDuration = (d) =>
    !d
      ? "—"
      : `${d.days ? d.days + "d " : ""}${d.hours ? d.hours + "h " : ""}${d.minutes ? d.minutes + "m" : ""
      }`;

  // backend-compatible keys, pretty labels
  const priorityColors = {
    Urgent: "text-red-500",
    Mare: "text-orange-500",
    Medie: "text-yellow-500",
    Mica: "text-green-500", // ✅ backend value
  };

  const priorityLabels = {
    Urgent: "Urgent",
    Mare: "Mare",
    Medie: "Medie",
    Mica: "Mică", // ✅ shown to user with diacritic
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className={`p-8 rounded-2xl w-[900px] max-w-4xl mx-auto mt-20 outline-none border transition-all duration-300 shadow-xl ${darkMode
        ? "bg-gradient-to-b from-gray-900 to-gray-950 text-gray-100 border-gray-800 shadow-[0_0_25px_rgba(0,0,0,0.5)]"
        : "bg-gradient-to-b from-white to-gray-50 text-gray-900 border-gray-300 shadow-[0_0_25px_rgba(0,0,0,0.15)]"
        }`}

      overlayClassName={`fixed inset-0 flex justify-center items-start z-50 backdrop-blur-sm transition-colors duration-300 ${darkMode ? "bg-black/70" : "bg-gray-900/40"
        }`}
    >

      {/* HEADER */}
      <div
        className={`flex justify-between items-center border-b pb-3 mb-5 ${darkMode ? "border-gray-700" : "border-gray-200"
          }`}
      >
        <h3
          className={`text-xl font-semibold flex items-center gap-2 ${darkMode ? "text-blue-400" : "text-blue-600"
            }`}
        >
          📋 {localTask.title}
        </h3>

        <button
          onClick={onClose}
          className={`transition ${darkMode
            ? "text-red-400 hover:text-red-600"
            : "text-red-500 hover:text-red-700"
            }`}
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* CONTENT */}
      <div
        className={`space-y-4 ${darkMode ? "text-gray-300" : "text-gray-900/95"
          }`}
      >
        {/* --- Description --- */}
        <div className="relative group transition-all duration-300">
          <div className="flex justify-between items-center mb-2">
            <h4
              className={`text-base font-semibold flex items-center gap-2 ${darkMode ? "text-gray-100" : "text-gray-800"
                }`}
            >
              Descriere
            </h4>

            {role === "Admin" && !editingDesc && (
              <button
                onClick={() => setEditingDesc(true)}
                className={`flex items-center gap-1 px-2 py-1 rounded-md transition-all duration-200 ${darkMode
                  ? "bg-blue-500/10 hover:bg-blue-500/20 text-blue-300"
                  : "bg-blue-100 hover:bg-blue-200 text-blue-700"
                  }`}
                title="Editează descrierea"
              >

                <PencilLine className="w-4 h-4" />
                <span className="text-sm font-medium">Editează</span>
              </button>
            )}
          </div>

          {/* DESCRIPTION CONTAINER */}
          <div
            className={`relative rounded-2xl border transition-all duration-300 ease-in-out ${editingDesc
              ? "border-blue-400 ring-2 ring-blue-300 shadow-[0_0_18px_rgba(59,130,246,0.4)] scale-[1.01]"
              : darkMode
                ? "border-gray-700 hover:border-blue-400/40 hover:shadow-[0_0_14px_rgba(37,99,235,0.25)]"
                : "border-gray-400 hover:border-blue-300/70 hover:shadow-[0_0_18px_rgba(59,130,246,0.25)]"
              } ${darkMode
                ? "bg-gradient-to-b from-gray-800 to-gray-900"
                : "bg-gradient-to-b from-gray-50 to-white"
              } ${!editingDesc && (!localTask.description || localTask.description.length < 80)
                ? "opacity-95 scale-[0.995]"
                : ""
              }`}

            style={{
              minHeight: editingDesc
                ? "160px"
                : !localTask.description
                  ? "100px"
                  : localTask.description.length < 80
                    ? "90px"
                    : localTask.description.length < 200
                      ? "120px"
                      : "150px",
              transition: "min-height 0.3s ease",
            }}
          >

            {editingDesc ? (
              <div className="flex flex-col h-full p-4">
                <textarea
                  value={localTask.description || ""}
                  onChange={(e) =>
                    setLocalTask((prev) => ({ ...prev, description: e.target.value }))
                  }
                  onKeyDown={(e) => {
                    if (e.ctrlKey && e.key === "Enter") {
                      e.preventDefault();
                      saveDescription();
                    } else if (e.key === "Escape") {
                      setEditingDesc(false);
                    }
                  }}
                  placeholder="Scrie detaliile task-ului aici..."
                  className={`flex-1 w-full resize-none rounded-xl p-3 text-[15px] leading-relaxed outline-none transition custom-scrollbar border focus:ring-2 focus:ring-blue-500 ${darkMode
                    ? "text-gray-100 bg-gray-800/70 border-gray-600 focus:bg-gray-800/90 placeholder-gray-500"
                    : "text-gray-800 bg-[#f9fafb] border-gray-300 focus:bg-white focus:border-blue-400 placeholder-gray-600"
                    }`}
                  style={{
                    lineHeight: "1.65",
                    scrollbarGutter: "stable both-edges",
                    minHeight: "180px",
                    maxHeight: "400px",
                    overflowY: "auto",
                  }}
                />

                <div className="flex justify-end gap-3 mt-3">
                  <button
                    onClick={() => setEditingDesc(false)}
                    className={`flex items-center gap-1 text-sm px-4 py-1.5 rounded-lg font-medium transition ${darkMode
                      ? "bg-gray-600 hover:bg-gray-500 text-white"
                      : "bg-gray-300 hover:bg-gray-400 text-gray-900"
                      }`}
                  >
                    <X className="w-4 h-4" /> Anulare
                  </button>

                  <button
                    onClick={saveDescription}
                    className={`flex items-center gap-1 text-sm px-4 py-1.5 rounded-lg font-medium transition ${darkMode
                      ? "bg-blue-700 hover:bg-blue-600 text-white"
                      : "bg-blue-600 hover:bg-blue-700 text-white"
                      }`}
                  >
                    <Check className="w-4 h-4" /> Salvează
                  </button>
                </div>

              </div>
            ) : (
              <div
                onDoubleClick={() => role === "Admin" && setEditingDesc(true)}
                className={`absolute inset-0 p-4 bottom-3 overflow-y-auto custom-scrollbar cursor-text 
  text-[15px] leading-relaxed rounded-2xl bg-transparent backdrop-blur-sm 
  ${darkMode ? "text-gray-200" : "text-gray-800"}`}
                style={{
                  scrollbarGutter: "stable both-edges",
                  wordBreak: "break-word",
                }}
              >
                {localTask.description ? (
                  <p className="whitespace-pre-wrap">
                    {localTask.description}
                  </p>
                ) : (
                  <div
                    className={`italic text-center transition-colors ${darkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                  >
                    — Fără descriere —
                    <br />
                    <span
                      className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"
                        }`}
                    >
                      {role === "Admin" && "(dublu click pentru editare)"}
                    </span>
                  </div>

                )}
              </div>
            )}
          </div>

        </div>

        <div
          className={`grid grid-cols-2 gap-x-6 gap-y-3 text-sm mt-6 pt-4 pb-4 border-y 
    ${darkMode ? "border-gray-700" : "border-gray-500"}`}
        >
          {/* Creator */}
          <div className="flex items-center gap-2">
            <User className={`w-4 h-4 ${darkMode ? "text-gray-400" : "text-gray-500"}`} />
            <span>
              <strong>Creat de:</strong> {localTask.adminName || "—"}
            </span>
          </div>

          {/* Atribuit către */}
          <div className="flex items-center gap-2 relative">
            <User className={`w-4 h-4 ${darkMode ? "text-gray-400" : "text-gray-500"}`} />
            <strong>Atribuit către:</strong>

            {role === "Admin" ? (
              assignOpen ? (
                <div className="relative ml-1">
                  <input
                    autoFocus
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onBlur={() => setTimeout(() => setAssignOpen(false), 200)}
                    placeholder="Caută utilizator..."
                    className={`w-44 px-2 py-1.5 text-xs rounded-lg border shadow-sm focus:outline-none 
  focus:ring-2 focus:ring-blue-400 transition-all
  ${darkMode
                        ? "bg-gray-800 border-blue-500 text-gray-100"
                        : "bg-blue-50/70 border-blue-400 text-gray-900"
                      }`}

                  />

                  {assignOpen &&
                    users.length > 0 &&
                    ReactDOM.createPortal(
                      <div
                        className={`fixed backdrop-blur-xl border rounded-xl shadow-[0_4px_24px_rgba(0,0,0,0.25)] 
  overflow-y-auto max-h-44 z-[9999] animate-fade-in transition-all duration-200
  scrollbar-thin scrollbar-track-transparent 
  [&::-webkit-scrollbar]:w-1.5 
  [&::-webkit-scrollbar-thumb]:rounded-full
  ${darkMode
                            ? "bg-gray-800/80 border-gray-700 [&::-webkit-scrollbar-thumb]:bg-blue-600"
                            : "bg-white/80 border-gray-200 [&::-webkit-scrollbar-thumb]:bg-blue-400"
                          }`}

                        style={{
                          top: dropdownPosition.top + "px",
                          left: dropdownPosition.left + "px",
                          width: "185px",
                        }}
                      >
                        {users
                          .filter((u) =>
                            u.name
                              .toLowerCase()
                              .includes(searchTerm.toLowerCase())
                          )
                          .map((u) => (
                            <div
                              key={u.id}
                              onMouseDown={() =>
                                assignUserToTask(localTask.id, u.id, u.name)
                              }
                              className={`px-3 py-2 text-sm cursor-pointer flex items-center gap-2 transition-colors duration-150
  ${darkMode
                                  ? "text-gray-100 hover:bg-blue-900/50"
                                  : "text-gray-800 hover:bg-blue-100/80"
                                }`}

                            >
                              <User className="w-4 h-4 text-blue-500 opacity-80" />
                              <span className="truncate">{u.name}</span>
                            </div>
                          ))}
                      </div>,
                      document.body
                    )}
                </div>
              ) : (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    const rect = e.currentTarget.getBoundingClientRect();

                    const modalContent = e.currentTarget.closest(".ReactModal__Content");
                    const scrollY = modalContent ? modalContent.scrollTop : 0;
                    const modalRect = modalContent ? modalContent.getBoundingClientRect() : { top: 0 };

                    // 🔧 ha nincs görgetés, modalRect.top ne legyen beleszámítva
                    const topOffset = scrollY > 0 ? modalRect.top : 0;

                    setDropdownPosition({
                      top: rect.bottom + scrollY + topOffset + 4,
                      left: rect.left + window.scrollX,
                    });

                    setAssignOpen(true);
                  }}


                  className={`flex items-center gap-1 px-2 py-1 rounded-md cursor-pointer transition ${darkMode
                    ? "text-blue-300 bg-gray-800/50 hover:bg-gray-900"
                    : "text-blue-800 bg-blue-100/60 border border-blue-400 hover:bg-blue-200 hover:border-blue-500"
                    }`}
                  title="Atribuie utilizator (Admin only)"
                >
                  <User className={`w-4 h-4 ${darkMode ? "text-blue-300" : "text-blue-600"}`} />
                  <span className="truncate max-w-[120px]">
                    {localTask.agentName || "—"}
                  </span>
                </div>
              )
            ) : (
              <span
                className={`ml-1 font-semibold ${darkMode ? "text-blue-300" : "text-blue-800"
                  }`}
              >
                {localTask.agentName || "—"}
              </span>

            )}
          </div>

          {/* DATES */}
          <div className="flex items-center gap-2">
            <Clock className={`w-4 h-4 ${darkMode ? "text-gray-400" : "text-gray-500"}`} />
            <span>
              <strong>Creat la:</strong> {formatDate(localTask.createdAt)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Activity className={`w-4 h-4 ${darkMode ? "text-gray-400" : "text-gray-500"}`} />
            <span>
              <strong>Început la:</strong> {formatDate(localTask.startTime)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className={`w-4 h-4 ${darkMode ? "text-gray-400" : "text-gray-500"}`} />
            <span>
              <strong>Finalizat la:</strong> {formatDate(localTask.completedAt)}
            </span>
          </div>

          {/* Deadline */}
          <div className="flex items-center gap-2">
            <Calendar className={`w-4 h-4 ${darkMode ? "text-gray-400" : "text-gray-500"}`} />
            <strong>Deadline:</strong>

            {role === "Admin" ? (
              <div className="relative flex items-center gap-1 text-xs text-red-500 whitespace-nowrap">
                <DatePicker
                  selected={
                    localTask.deadline
                      ? new Date(
                        localTask.deadline.endsWith("Z")
                          ? localTask.deadline
                          : localTask.deadline + "Z"
                      )
                      : null
                  }
                  onChange={(date) => {
                    if (!date) return;
                    updateDeadline(date);
                  }}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="yyyy-MM-dd HH:mm"
                  popperPlacement="bottom-end"
                  portalId="datepicker-root"
                  className="bg-transparent text-red-500 text-xs font-medium cursor-pointer w-[110px]"
                  calendarClassName={`shadow-lg rounded-lg border ${darkMode
                    ? "border-gray-700 bg-gray-800 text-gray-100"
                    : "border-gray-300 bg-white text-gray-900"
                    }`}
                  customInput={
                    <div
                      className={`w-full flex items-center justify-end py-1 px-2 rounded-md cursor-pointer transition select-none ${darkMode
                        ? "hover:bg-gray-800 text-red-400"
                        : "hover:bg-orange-200 text-orange-600"
                        }`}
                      title="Modifică deadline (Admin only)"
                    >
                      <span className="text-red-500">📅</span>
                      <span className="text-red-500 text-xs font-medium">
                        {localTask.deadline
                          ? new Date(
                            localTask.deadline.endsWith("Z")
                              ? localTask.deadline
                              : localTask.deadline + "Z"
                          ).toLocaleString("ro-RO", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                          : "—"}
                      </span>
                    </div>
                  }
                />
              </div>
            ) : (
              <span>
                {localTask.deadline
                  ? new Date(
                    localTask.deadline.endsWith("Z")
                      ? localTask.deadline
                      : localTask.deadline + "Z"
                  ).toLocaleString("ro-RO", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                  : "—"}
              </span>
            )}
          </div>

          {/* SLA + Worked */}
          <div className="flex items-center gap-2">
            <Timer className={`w-4 h-4 ${darkMode ? "text-gray-400" : "text-gray-500"}`} />
            <span>
              <strong>SLA:</strong> {formatDuration(sla)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Timer className={`w-4 h-4 ${darkMode ? "text-gray-400" : "text-gray-500"}`} />
            <span>
              <strong>Timp lucrat:</strong> {formatDuration(worked)}
            </span>
          </div>

          {/* Priority */}
          <div className="flex items-center gap-2 relative">
            <AlertTriangle className={`w-4 h-4 ${darkMode ? "text-gray-400" : "text-gray-500"}`} />
            <strong>Prioritate:</strong>
            {role === "Admin" ? (
              <span
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();

                  const modalContent = e.currentTarget.closest(".ReactModal__Content");
                  const scrollY = modalContent ? modalContent.scrollTop : 0;
                  const modalRect = modalContent ? modalContent.getBoundingClientRect() : { top: 0 };

                  const topOffset = scrollY > 0 ? modalRect.top : 0;

                  setPriorityDropdownPos({
                    top: rect.bottom + scrollY + topOffset + 4,
                    left: rect.left + window.scrollX,
                  });

                  setPriorityOpen(true);
                }}

                className={`ml-1 cursor-pointer font-semibold hover:underline ${priorityColors[localTask.priority]}`}
              >
                {priorityLabels[localTask.priority] || localTask.priority}
              </span>

            ) : (
              <span
                className={`ml-1 font-semibold ${priorityColors[localTask.priority]}`}
              >
                {priorityLabels[localTask.priority] || localTask.priority}
              </span>

            )}
            {priorityOpen &&
              ReactDOM.createPortal(
                <div
                  className="priority-dropdown fixed bg-white/95 dark:bg-gray-800/95 border border-gray-300 dark:border-gray-700 rounded-lg shadow-lg max-h-44 overflow-y-auto z-[9999] backdrop-blur-md" style={{
                    top: priorityDropdownPos.top + 24,
                    left: priorityDropdownPos.left,
                    width: 180,
                  }}
                >
                  {["Urgent", "Mare", "Medie", "Mica"].map((p) => (
                    <div
                      key={p}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        updatePriority(localTask.id, p);
                      }}
                      className={`px-3 py-2 text-sm cursor-pointer flex items-center gap-2 hover:bg-blue-100/80 dark:hover:bg-blue-900/50 ${priorityColors[p]}`}
                    >
                      {priorityLabels[p]}
                    </div>
                  ))}
                </div>,
                document.body
              )}
          </div>

          {/* Status */}
          <div className="flex items-center gap-2">
            {localTask.status === "Nou" && (
              <Flag className={`w-4 h-4 ${darkMode ? "text-gray-400" : "text-gray-500"}`} />
            )}
            {localTask.status === "Vazut" && (
              <Eye className={`w-4 h-4 ${darkMode ? "text-gray-400" : "text-gray-500"}`} />
            )}
            {localTask.status === "InProductie" && (
              <Loader2 className={`w-4 h-4 animate-spin-slow ${darkMode ? "text-blue-400" : "text-blue-500"}`} />
            )}
            {localTask.status === "Finalizat" && (
              <CheckCircle className={`w-4 h-4 ${darkMode ? "text-green-400" : "text-green-500"}`} />
            )}
            <span>
              <strong>Status:</strong>{" "}
              <span className={`capitalize font-medium ${darkMode ? "text-blue-400" : "text-blue-500"}`}>
                {localTask.status === "Nou"
                  ? "Nou"
                  : localTask.status === "Vazut"
                    ? "Văzut"
                    : localTask.status === "InProductie"
                      ? "În producție"
                      : localTask.status === "Finalizat"
                        ? "Finalizat"
                        : localTask.status}
              </span>
            </span>

          </div>
        </div>

        {localTask.feedback && (
          <div className="mt-4">
            <strong>Feedback:</strong>
            <p
              className={`text-sm mt-1 ${darkMode ? "text-gray-300" : "text-gray-700"}`}
              title={localTask.feedback}
            >
              {localTask.feedback}
            </p>
          </div>
        )}



        {role === "Admin" && localTask.status === "Finalizat" && (
          <div className="mt-4 pt-4 flex justify-end">
            <button
              onClick={() => setConfirmArchiveOpen(true)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition 
        ${darkMode
                  ? "bg-gray-700 hover:bg-gray-600 text-red-300"
                  : "bg-red-100 hover:bg-red-200 text-red-700 border border-red-400"}`}
            >
              🗄️ Arhivează task
            </button>
          </div>
        )}

        {/* 🔹 Confirm modal */}
        <ConfirmArchiveModal
          isOpen={confirmArchiveOpen}
          onClose={() => setConfirmArchiveOpen(false)}
          loading={archiving}
          onConfirm={async () => {
            setArchiving(true);
            try {
              const res = await fetch(`${API_BASE_URL}/api/tasks/${localTask.id}/archive`, {
                method: "PUT",
                headers: { Authorization: `Bearer ${token}` },
              });
              const data = await res.json();
              if (!res.ok || data.success === false) {
                toast.error(data.message || "Eroare la arhivare.");
                return;
              }
              toast.success("Task arhivat cu succes!");
              if (typeof onTaskUpdated === "function") {
                onTaskUpdated(localTask.id, { archived: true });
              }
              setConfirmArchiveOpen(false);
              onClose();
            } catch (err) {
              console.error("⚠️ Archive error:", err);
              toast.error("Eroare la server.");
            } finally {
              setArchiving(false);
            }
          }}
        />

      </div>

    </Modal>
  );
};

export default TaskDetailsModal;
